import amplitudeNotificationTracking from '@/services/analytics/amplitude/amplitudeNotificationTracking';
import googleAnalyticsService from '@/services/analytics/ga/googleAnalyticsService';
import log from '@/services/logger/log';

const isNotificationSupported = () => {
    return typeof Notification !== 'undefined';
};

const isPermissionsApiSupported = () => {
    return navigator?.permissions?.query;
};

const getNotificationPermissionStatus = async () => {
    if (!isNotificationSupported()) return 'unsupported';

    // Try to use the modern Permissions API first
    if (isPermissionsApiSupported()) {
        try {
            const status = await navigator.permissions.query({name: 'notifications'});

            return status.state;
        } catch (error) {
            log.debug('Permissions API not available, falling back to Notification.permission', error);
        }
    }

    // Fallback to the legacy Notification.permission
    return Notification.permission;
};

const checkIfNotificationIsGranted = async () => {
    const permission = await getNotificationPermissionStatus();

    return permission === 'granted';
};

const checkIfNotificationIsDenied = async () => {
    const permission = await getNotificationPermissionStatus();

    return permission === 'denied';
};

const checkIfNotificationIsDefault = async () => {
    const permission = await getNotificationPermissionStatus();

    return permission === 'default' || permission === 'prompt';
};

const trackNotificationPermission = (isGranted) => {
    if (isGranted) {
        amplitudeNotificationTracking.trackSubscribeToReceiveMessages();
        googleAnalyticsService.trackAllowPushNotifications();
        log.debug('Notifications are allowed now.');
    } else {
        amplitudeNotificationTracking.trackUnsubscribeToReceiveMessages();
        googleAnalyticsService.trackDisallowPushNotifications();
    }
};

const askPermission = async ({onGranted, onDenied} = {}) => {
    if (await checkIfNotificationIsGranted()) {
        onGranted?.();
        return 'granted';
    }

    const permission = await Notification.requestPermission();

    if (permission === 'granted') {
        onGranted?.();
    } else {
        onDenied?.();
    }
    return permission;
};

const requestNotificationPermission = async () => {
    return await askPermission({
        onGranted: () => trackNotificationPermission(true),
        onDenied: () => {
            trackNotificationPermission(false);
            throw new Error('Notifications are denied.');
        },
    });
};

export default {
    checkIfNotificationIsGranted,
    checkIfNotificationIsDenied,
    checkIfNotificationIsDefault,
    requestNotificationPermission,
};
